export interface UserInfo {
  id?: number;

  title?: string | null;
  first_name?: string | null;
  last_name?: string | null;

  username: string; // required

  designation?: string | null;
  email?: string | null;

  groups?: number[]; // array of group IDs
  accounts?: number[]; // array of account IDs

  active_account?: number | null;

  photo?: string | null; // URL or base64
  birthdate?: string | null; // date string
  date_joined?: string | null;

  national_provider_identifier?: string | null;
  phone_number?: string | null;
  first_login?: boolean | null;
  // One-time onboarding cues already shown, as cue name -> ISO timestamp; stamps are never cleared.
  onboarding_seen?: Record<string, string> | null;
  is_active?: boolean | null;
  is_staff?: boolean | null;
}

export interface OAuthStatePayload {
  state: string;
  nonce: string;
}

export interface AuthState {
  userId: number | null;
  isLoading: boolean;
  firstTimeLogin: boolean;
  onboardingSeen: Record<string, string>;
  isLogoutLoading: boolean;
  account_id: number | null;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  groups: any[];
  photo: string;
  designation: string;
  phoneNumber: string;
  title: string;
  dob: string;
  loggedIn: boolean;
  message: string;
  userWasLoggedIn: boolean;
  checkLoggedInUser: boolean;
  isAuthorized: boolean;
  doLogout: boolean;
  isAuthInProgress: boolean;
  flowState: string;
  oauthState: string | null;
  oidcNonce: string | null;
  oidcErrorDetail: string;
  userPermissions: UserPermissions | {};
  isPermissionsLoaded: boolean;
}

// Enumeration of possible OIDC flow states
export const oidcFlowState = Object.freeze({
  NOT_STARTED: "NOT_STARTED",
  STARTED: "STARTED",
  INITIALIZED: "INITIALIZED",
  COMPLETED: "COMPLETED",
  ERROR: "ERROR",
});

export interface LoginData {
  username: string;
  email: string;
}

export interface OidcLoginCompleteData {
  state: string;
  code: string;
  redirect_uri: string;
}

export interface OidcErrorPayload {
  status?: number;
  message?: string;
}

export interface UserPermissions {
  add_account: boolean;
  add_allergy: boolean;
  add_alternatelabrequestid: boolean;
  add_alternatelabtestrequestid: boolean;
  add_appointment: boolean;
  add_backgroundprocess: boolean;
  add_billinghistory: boolean;
  add_caregap: boolean;
  add_ckdstatus: boolean;
  add_claim: boolean;
  add_claimresponse: boolean;
  add_clinicalfact: boolean;
  add_condition: boolean;
  add_cptcodes: boolean;
  add_document: boolean;
  add_encounter: boolean;
  add_encounterrecording: boolean;
  add_encountersoapnote: boolean;
  add_encounterv2: boolean;
  add_familyhistory: boolean;
  add_favorite: boolean;
  add_feedback: boolean;
  add_icd10codes: boolean;
  add_imagingcodes: boolean;
  add_integrationplugin: boolean;
  add_integrationusercredential: boolean;
  add_invitation: boolean;
  add_labcodes: boolean;
  add_labobservation: boolean;
  add_labobservationtestmapping: boolean;
  add_laborder: boolean;
  add_labprovider: boolean;
  add_labrequest: boolean;
  add_labtest: boolean;
  add_labtestrequest: boolean;
  add_location: boolean;
  add_lookup: boolean;
  add_maintenancehistory: boolean;
  add_medicalencounterhistory: boolean;
  add_medicalhistory: boolean;
  add_medicalreporthistory: boolean;
  add_medication: boolean;
  add_normalizedlabobservation: boolean;
  add_order: boolean;
  add_otherencounter: boolean;
  add_passwordresettoken: boolean;
  add_patient: boolean;
  add_payer: boolean;
  add_pharmacy: boolean;
  add_phqassessment: boolean;
  add_phqquestion: boolean;
  add_phqresponse: boolean;
  add_pifstatus: boolean;
  add_practice: boolean;
  add_practitioner: boolean;
  add_prescription: boolean;
  add_procedurecodes: boolean;
  add_recordingconsent: boolean;
  add_referralcodes: boolean;
  add_riskfactor: boolean;
  add_smartalert: boolean;
  add_smartalertauditlog: boolean;
  add_smartrecommendations: boolean;
  add_socialhistory: boolean;
  add_surgicalhistory: boolean;
  add_term: boolean;
  add_user: boolean;
  add_useraccountextension: boolean;
  add_usergroupmembership: boolean;
  add_vital: boolean;
  can_activate_user: boolean;
  can_deactivate_user: boolean;
  can_invite_user: boolean;
  can_modify_user_roles: boolean;
  can_read_encounter_data: boolean;
  can_read_patient_data: boolean;
  can_read_practice_billing: boolean;
  can_record_encounter: boolean;
  can_remove_user: boolean;
  can_sign_encounter_note: boolean;
  can_update_patient_summary: boolean;
  can_update_smart_alert_reminder: boolean;
  can_update_smart_alert_status: boolean;
  can_write_patient_intake: boolean;
  change_account: boolean;
  change_allergy: boolean;
  change_alternatelabrequestid: boolean;
  change_alternatelabtestrequestid: boolean;
  change_appointment: boolean;
  change_backgroundprocess: boolean;
  change_billinghistory: boolean;
  change_caregap: boolean;
  change_ckdstatus: boolean;
  change_claim: boolean;
  change_claimresponse: boolean;
  change_clinicalfact: boolean;
  change_condition: boolean;
  change_cptcodes: boolean;
  change_document: boolean;
  change_encounter: boolean;
  change_encounterrecording: boolean;
  change_encountersoapnote: boolean;
  change_encounterv2: boolean;
  change_familyhistory: boolean;
  change_favorite: boolean;
  change_feedback: boolean;
  change_icd10codes: boolean;
  change_imagingcodes: boolean;
  change_integrationplugin: boolean;
  change_integrationusercredential: boolean;
  change_invitation: boolean;
  change_labcodes: boolean;
  change_labobservation: boolean;
  change_labobservationtestmapping: boolean;
  change_laborder: boolean;
  change_labprovider: boolean;
  change_labrequest: boolean;
  change_labtest: boolean;
  change_labtestrequest: boolean;
  change_location: boolean;
  change_lookup: boolean;
  change_maintenancehistory: boolean;
  change_medicalencounterhistory: boolean;
  change_medicalhistory: boolean;
  change_medicalreporthistory: boolean;
  change_medication: boolean;
  change_normalizedlabobservation: boolean;
  change_order: boolean;
  change_otherencounter: boolean;
  change_passwordresettoken: boolean;
  change_patient: boolean;
  change_payer: boolean;
  change_pharmacy: boolean;
  change_phqassessment: boolean;
  change_phqquestion: boolean;
  change_phqresponse: boolean;
  change_pifstatus: boolean;
  change_practice: boolean;
  change_practitioner: boolean;
  change_prescription: boolean;
  change_procedurecodes: boolean;
  change_recordingconsent: boolean;
  change_referralcodes: boolean;
  change_riskfactor: boolean;
  change_smartalert: boolean;
  change_smartalertauditlog: boolean;
  change_smartrecommendations: boolean;
  change_socialhistory: boolean;
  change_surgicalhistory: boolean;
  change_term: boolean;
  change_user: boolean;
  change_useraccountextension: boolean;
  change_usergroupmembership: boolean;
  change_vital: boolean;
  delete_account: boolean;
  delete_allergy: boolean;
  delete_alternatelabrequestid: boolean;
  delete_alternatelabtestrequestid: boolean;
  delete_appointment: boolean;
  delete_backgroundprocess: boolean;
  delete_billinghistory: boolean;
  delete_caregap: boolean;
  delete_ckdstatus: boolean;
  delete_claim: boolean;
  delete_claimresponse: boolean;
  delete_clinicalfact: boolean;
  delete_condition: boolean;
  delete_cptcodes: boolean;
  delete_document: boolean;
  delete_encounter: boolean;
  delete_encounterrecording: boolean;
  delete_encountersoapnote: boolean;
  delete_encounterv2: boolean;
  delete_familyhistory: boolean;
  delete_favorite: boolean;
  delete_feedback: boolean;
  delete_icd10codes: boolean;
  delete_imagingcodes: boolean;
  delete_integrationplugin: boolean;
  delete_integrationusercredential: boolean;
  delete_invitation: boolean;
  delete_labcodes: boolean;
  delete_labobservation: boolean;
  delete_labobservationtestmapping: boolean;
  delete_laborder: boolean;
  delete_labprovider: boolean;
  delete_labrequest: boolean;
  delete_labtest: boolean;
  delete_labtestrequest: boolean;
  delete_location: boolean;
  delete_lookup: boolean;
  delete_maintenancehistory: boolean;
  delete_medicalencounterhistory: boolean;
  delete_medicalhistory: boolean;
  delete_medicalreporthistory: boolean;
  delete_medication: boolean;
  delete_normalizedlabobservation: boolean;
  delete_order: boolean;
  delete_otherencounter: boolean;
  delete_passwordresettoken: boolean;
  delete_patient: boolean;
  delete_payer: boolean;
  delete_pharmacy: boolean;
  delete_phqassessment: boolean;
  delete_phqquestion: boolean;
  delete_phqresponse: boolean;
  delete_pifstatus: boolean;
  delete_practice: boolean;
  delete_practitioner: boolean;
  delete_prescription: boolean;
  delete_procedurecodes: boolean;
  delete_recordingconsent: boolean;
  delete_referralcodes: boolean;
  delete_riskfactor: boolean;
  delete_smartalert: boolean;
  delete_smartalertauditlog: boolean;
  delete_smartrecommendations: boolean;
  delete_socialhistory: boolean;
  delete_surgicalhistory: boolean;
  delete_term: boolean;
  delete_user: boolean;
  delete_useraccountextension: boolean;
  delete_usergroupmembership: boolean;
  delete_vital: boolean;
  view_account: boolean;
  view_allergy: boolean;
  view_alternatelabrequestid: boolean;
  view_alternatelabtestrequestid: boolean;
  view_appointment: boolean;
  view_backgroundprocess: boolean;
  view_billinghistory: boolean;
  view_caregap: boolean;
  view_ckdstatus: boolean;
  view_claim: boolean;
  view_claimresponse: boolean;
  view_clinicalfact: boolean;
  view_condition: boolean;
  view_cptcodes: boolean;
  view_document: boolean;
  view_encounter: boolean;
  view_encounterrecording: boolean;
  view_encountersoapnote: boolean;
  view_encounterv2: boolean;
  view_familyhistory: boolean;
  view_favorite: boolean;
  view_feedback: boolean;
  view_icd10codes: boolean;
  view_imagingcodes: boolean;
  view_integrationplugin: boolean;
  view_integrationusercredential: boolean;
  view_invitation: boolean;
  view_labcodes: boolean;
  view_labobservation: boolean;
  view_labobservationtestmapping: boolean;
  view_laborder: boolean;
  view_labprovider: boolean;
  view_labrequest: boolean;
  view_labtest: boolean;
  view_labtestrequest: boolean;
  view_location: boolean;
  view_lookup: boolean;
  view_maintenancehistory: boolean;
  view_medicalencounterhistory: boolean;
  view_medicalhistory: boolean;
  view_medicalreporthistory: boolean;
  view_medication: boolean;
  view_normalizedlabobservation: boolean;
  view_order: boolean;
  view_otherencounter: boolean;
  view_passwordresettoken: boolean;
  view_patient: boolean;
  view_payer: boolean;
  view_pharmacy: boolean;
  view_phqassessment: boolean;
  view_phqquestion: boolean;
  view_phqresponse: boolean;
  view_pifstatus: boolean;
  view_practice: boolean;
  view_practitioner: boolean;
  view_prescription: boolean;
  view_procedurecodes: boolean;
  view_recordingconsent: boolean;
  view_referralcodes: boolean;
  view_riskfactor: boolean;
  view_smartalert: boolean;
  view_smartalertauditlog: boolean;
  view_smartrecommendations: boolean;
  view_socialhistory: boolean;
  view_surgicalhistory: boolean;
  view_term: boolean;
  view_user: boolean;
  view_useraccountextension: boolean;
  view_usergroupmembership: boolean;
  view_vital: boolean;
}
